import type { ActionContextPayload } from '@unifyapps/defs/hooks/useBlockEvents';
import { useTranslation } from '@unifyapps/i18n/client';
import { useSnackbar } from '@unifyapps/ui/components/Snackbar';
import { useCallback } from 'react';
import { useGetNoCodeComputedData } from '../../../computeContext/useGetNoCodeComputedData';
import { exportSpreadsheet } from './helpers/exportSpreadsheet';
import type { ExportDataPayload } from './types';

export const useExportJsonData = () => {
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation(['blocks']);
  const { getNoCodeComputedData } = useGetNoCodeComputedData();

  return useCallback(
    async (payload: ExportDataPayload, actionContext: ActionContextPayload) => {
      if (!payload.data) {
        showSnackbar({ color: 'error', title: t('blocks:Actions.ExportData.NoData') });
        return;
      }

      const computedData = getNoCodeComputedData(payload, actionContext) as ExportDataPayload & {
        data: unknown;
      };

      if (!Array.isArray(computedData.data)) {
        showSnackbar({
          color: 'error',
          title: t('blocks:Actions.ExportData.InvalidDataFormat.title'),
          description: t('blocks:Actions.ExportData.InvalidDataFormat.description'),
        });
        return;
      }

      switch (computedData.fileType) {
        case 'CSV':
        case 'XLS':
        case 'XLSX':
          await exportSpreadsheet({
            jsonData: computedData.data as Record<string, object>[],
            filename: computedData.fileName ?? 'Untitled',
            fileType: computedData.fileType,
            sheetName: computedData.sheetName ?? 'Sheet1',
          });
          break;
        case undefined:
        default:
          showSnackbar({
            color: 'error',
            title: t('blocks:Actions.ExportData.InvalidFileType.title', {
              fileType: computedData.fileType,
            }),
            description: t('blocks:Actions.ExportData.InvalidFileType.description'),
          });
      }
    },
    [getNoCodeComputedData, showSnackbar, t],
  );
};
